/**
 * SessionExpiredDrawer
 *
 * Two variants share the same drawer chrome:
 *   - 'session-expired'   (default): both silent + full re-auth failed, or the
 *                                    user cancelled the system browser on
 *                                    native. CTAs: Sign in again / Continue as
 *                                    guest. The session has been cleared.
 *   - 'service-unavailable':         the proactive token refresh circuit
 *                                    breaker tripped on a non-grant error
 *                                    (Logto 503 / network outage). The user is
 *                                    likely still authenticated. CTA: Retry
 *                                    (page reload). NO guest fallback — we
 *                                    don't want to discard a still-valid
 *                                    session.
 *
 * Separate from `SignedOutByOtherTabDrawer` — that one reacts to a cross-tab
 * sign-out event, this one reacts to invalid_grant recovery failures or
 * service outages. The triggers, lifecycles, and copy differ, so they are
 * kept as distinct drawers.
 *
 * State is owned by `AuthProvider` and passed in via props so the drawer is a
 * pure presentational component.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from '@components/ui/drawer';
import { Button } from '@components/ui/button';

type PendingAction = 'sign-in' | 'guest' | 'retry' | null;

export type SessionExpiredDrawerVariant = 'session-expired' | 'service-unavailable';

interface SessionExpiredDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which copy + button set to render. Defaults to 'session-expired'. */
  variant?: SessionExpiredDrawerVariant;
  /** Primary CTA for 'session-expired': redirect to Logto sign-in. */
  onSignIn: () => void;
  /** Secondary CTA for 'session-expired': dismiss, user stays as guest. */
  onContinueAsGuest: () => void;
  /**
   * Primary CTA for 'service-unavailable'. Defaults to a hard page reload.
   * May return a Promise; if it does, the spinner stays until it settles so
   * overrides that perform async work don't flash the button state. Overrides
   * that do NOT reload/unmount MUST return (or resolve) eventually, otherwise
   * the button remains disabled.
   */
  onRetry?: () => void | Promise<void>;
}

interface SessionExpiredDrawerBodyProps {
  onOpenChange: (open: boolean) => void;
  variant: SessionExpiredDrawerVariant;
  onSignIn: () => void;
  onContinueAsGuest: () => void;
  onRetry: () => void | Promise<void>;
}

/**
 * Inner body — owns the pending state. Lives inside DrawerContent, which vaul
 * unmounts on close, so `pendingAction` resets via unmount instead of an
 * effect (per meetball-react-patterns: prefer unmount/key over useEffect for
 * prop-driven state resets).
 */
function SessionExpiredDrawerBody({
  onOpenChange,
  variant,
  onSignIn,
  onContinueAsGuest,
  onRetry,
}: SessionExpiredDrawerBodyProps) {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const isPending = pendingAction !== null;

  const handleSignIn = () => {
    if (isPending) return;
    setPendingAction('sign-in');
    onOpenChange(false);
    onSignIn();
  };

  const handleContinueAsGuest = () => {
    if (isPending) return;
    setPendingAction('guest');
    onOpenChange(false);
    onContinueAsGuest();
  };

  const handleRetry = () => {
    if (isPending) return;
    setPendingAction('retry');
    // If onRetry returns a Promise (async retry that doesn't unmount), clear
    // pendingAction once it settles so the button becomes usable again.
    // The default onRetry is a sync page reload — the window unloads before
    // the `.finally` callback can run, so the reset is harmless for that
    // path. Wrap in Promise.resolve to tolerate either shape without a
    // branching try/catch.
    Promise.resolve()
      .then(() => onRetry())
      .catch((err: unknown) => {
        // Never let a rejected onRetry leave the spinner stuck — swallow the
        // rejection so the `.finally` runs. The caller is responsible for
        // surfacing their own error UI (retries don't have a natural error
        // channel on this drawer).
        console.error('[SessionExpiredDrawer] onRetry rejected:', err);
      })
      .finally(() => {
        setPendingAction(null);
      });
  };

  if (variant === 'service-unavailable') {
    return (
      <>
        <DrawerHeader>
          <DrawerTitle>Trouble reaching our servers</DrawerTitle>
          <DrawerDescription>
            Give it a moment, then try again. Your account stays signed in.
          </DrawerDescription>
        </DrawerHeader>
        <DrawerFooter>
          <Button
            onClick={handleRetry}
            disabled={isPending}
            className="bg-mb-yellow hover:bg-mb-yellow text-black font-semibold rounded-full px-8
            w-full h-12 shadow-glow-yellow transition-all duration-200 disabled:opacity-100"
          >
            {pendingAction === 'retry' ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>One sec</span>
              </span>
            ) : (
              'Retry'
            )}
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerHeader>
        <DrawerTitle>You&apos;ve been signed out</DrawerTitle>
        <DrawerDescription>
          Your session ended. Sign in again to pick up where you left off.
        </DrawerDescription>
      </DrawerHeader>
      <DrawerFooter>
        <Button
          onClick={handleSignIn}
          disabled={isPending}
          className="bg-mb-yellow hover:bg-mb-yellow text-black font-semibold rounded-full px-8
          w-full h-12 shadow-glow-yellow transition-all duration-200 disabled:opacity-100"
        >
          {pendingAction === 'sign-in' ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>One sec</span>
            </span>
          ) : (
            'Sign in again'
          )}
        </Button>
        <Button variant="ghost" onClick={handleContinueAsGuest} disabled={isPending}>
          {pendingAction === 'guest' ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>One sec</span>
            </span>
          ) : (
            'Continue as guest'
          )}
        </Button>
      </DrawerFooter>
    </>
  );
}

function defaultRetry(): void {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

export function SessionExpiredDrawer({
  open,
  onOpenChange,
  variant = 'session-expired',
  onSignIn,
  onContinueAsGuest,
  onRetry = defaultRetry,
}: SessionExpiredDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <SessionExpiredDrawerBody
          onOpenChange={onOpenChange}
          variant={variant}
          onSignIn={onSignIn}
          onContinueAsGuest={onContinueAsGuest}
          onRetry={onRetry}
        />
      </DrawerContent>
    </Drawer>
  );
}
